import { useState, useEffect, useCallback, useRef } from "react";
import { useParams, useNavigate, useSearchParams, Link } from "react-router-dom";
import API from "../lib/api";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import QuoteApprovalLinkButton from "../components/QuoteApprovalLinkButton";
import { PortalPushButton, PortalLiveBadge, ClientDecisionBanner } from "../components/quotes/PortalQuoteActions";
import ScopeDriftBanner from "../components/quotes/ScopeDriftBanner";
import CaveatLibraryPicker, { appendCaveat } from "../components/jobs/CaveatLibraryPicker";
import SaveLineMenu, { saveLineHandler } from "../components/jobs/SaveLineMenu";
import ScopeSetPicker from "../components/jobs/ScopeSets";
import QuoteEmailDialog from "../components/quotes/QuoteEmailDialog";
import AiScopeDialog from "../components/quotes/AiScopeDialog";
import NotesSection from "../components/NotesSection";
import { NoticeEditor, EMPTY_NOTICE } from "../components/NoticeEditor";
import { toast } from "sonner";
import {
  ArrowLeft, Plus, Trash, Receipt, FilePdf, Briefcase, Folder,
  Copy, PencilSimple, CheckCircle, Warning, Clock, UploadSimple, EnvelopeSimple, ArrowsClockwise, Sparkle
} from "@phosphor-icons/react";
import { ArrowCounterClockwise } from "@phosphor-icons/react";
import AddressAutocomplete from "../components/AddressAutocomplete";
import ReverseCalcDialog from "../components/ReverseCalcDialog";
import SupplierPicker from "../components/SupplierPicker";
import { Checkbox } from "../components/ui/checkbox";
import { fmtDate } from "../lib/dateUtil";

const PROPERTY_STATUSES = [
  { value: "tenanted", label: "Tenanted" },
  { value: "void", label: "Void" },
  { value: "vacant", label: "Vacant" },
  { value: "occupied", label: "Occupied" },
  { value: "under_renovation", label: "Under Renovation" },
  { value: "hmo", label: "HMO" },
  { value: "commercial", label: "Commercial" },
];

const STATUS_STYLES = {
  draft: "bg-slate-100 text-slate-700", sent: "bg-blue-100 text-blue-700",
  approved: "bg-emerald-100 text-emerald-700", declined: "bg-red-100 text-red-700",
  expired: "bg-amber-100 text-amber-700", converted: "bg-violet-100 text-violet-700",
  submitted: "bg-blue-100 text-blue-700", accepted: "bg-emerald-100 text-emerald-700",
};

const EMPTY_LINE = { item_name: "", description: "", quantity: 1, unit_cost: 0 };
// Quote notices carry no client-visibility flag — take tone/title/text from the request/job and nothing else
const noticeFromSource = (src, fallback) => (src?.text ? { ...EMPTY_NOTICE, tone: src.tone || "red", title: src.title || "", text: src.text } : fallback);
const copiedTerms = (src) => {
  const parts = [(src.scope_of_works || "").trim() && "Scope of works", (src.caveats || "").trim() && "caveats", src.notice?.text && "safety notice"].filter(Boolean);
  return parts.length ? parts.join(", ").replace(/, ([^,]*)$/, " and $1") : "";
};


export default function QuoteDetailPage() {
  const { quoteId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isNew = quoteId === "new";
  const jrIdParam = searchParams.get("jr_id") || "";
  const jobIdParam = searchParams.get("job_id") || "";
  const [linkedJob, setLinkedJob] = useState(null);
  // Phase 291 — /quotes/new?source_type=supplier|in_house switches the top
  // section between an in-house build and a passed-through supplier quote.
  const sourceTypeParam = searchParams.get("source_type") || "in_house";

  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(!isNew);
  const [saving, setSaving] = useState(false);
  const [reverseCalcOpen, setReverseCalcOpen] = useState(false);
  const [clients, setClients] = useState([]);
  const [contacts, setContacts] = useState([]);
  const [markupTemplates, setMarkupTemplates] = useState([]);
  const [quoteTemplates, setQuoteTemplates] = useState([]);
  const [showConvert, setShowConvert] = useState(false);
  const [showUnconvert, setShowUnconvert] = useState(false);
  const [unconverting, setUnconverting] = useState(false);

  const handleUnconvert = async () => {
    setUnconverting(true);
    try {
      const { data } = await API.post(`/quotes/${quoteId}/unconvert`);
      toast.success(data.message);
      setShowUnconvert(false);
      fetchQuote();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to undo conversion");
    } finally { setUnconverting(false); }
  };
  const [converting, setConverting] = useState(false);
  const [convertCreateWO, setConvertCreateWO] = useState(true);
  // Phase 262 — when this quote is for a specific Job Request
  const [linkedJR, setLinkedJR] = useState(null);
  const [scopeFrom, setScopeFrom] = useState(null);
  const [scopeLineMenu, setScopeLineMenu] = useState(null);
  const [caveatLineMenu, setCaveatLineMenu] = useState(null);
  const libraryCategory = linkedJob?.category || linkedJR?.category || "general";
  const [linkedJRId, setLinkedJRId] = useState("");
  // Phase 291 — Supplier-source quote: onboarded suppliers list + auto-create toggle
  const [onboardedSuppliers, setOnboardedSuppliers] = useState([]);

  // Client PDF branding dialog
  const [showBrandingDialog, setShowBrandingDialog] = useState(false);
  const [brandingForm, setBrandingForm] = useState({ company_name: "", tagline: "", logo_url: "", footer_text: "" });
  const [savedBranding, setSavedBranding] = useState(null);
  const [brandingLogoFile, setBrandingLogoFile] = useState(null);
  const [brandingUploading, setBrandingUploading] = useState(false);
  const brandingFileRef = useRef(null);

  // Email Quote (opt-in feature)
  const [emailEnabled, setEmailEnabled] = useState(false);
  const [showEmailDialog, setShowEmailDialog] = useState(false);
  const [aiKind, setAiKind] = useState("");

  // Form state
  const [form, setForm] = useState({
    title: "", client_id: "", client_name: "", client_email: "", client_phone: "", client_company: "",
    contact_id: "", contact_name: "", contact_color: "",
    property_name: "", street_address: "", town_city: "", county: "", postcode: "",
    property_status: "", tenant_name: "", tenant_phone: "",
    site_contact_name: "", site_contact_phone: "",
    markup_a_pct: 0, markup_b_pct: 0, markup_a_label: "Our Mark Up", markup_b_label: "Client Mark Up",
    line_items: [{ ...EMPTY_LINE, id: crypto.randomUUID() }],
    validity_days: 30, terms: "", notes: "", scope_of_works: "", caveats: "", notice: { ...EMPTY_NOTICE },
    vat_enabled: false, vat_rate: 0,
    images: [],
    // Phase 291 — supplier-source quote fields (default in_house for backward compat)
    source_type: sourceTypeParam === "supplier" ? "supplier" : "in_house",
    vendor_id: "", vendor_name: "", vendor_email: "", vendor_phone: "", vendor_company: "",
    create_supplier_record: false,
  });

  const fetchQuote = useCallback(async () => {
    if (isNew) return;
    setLoading(true);
    try {
      const { data } = await API.get(`/quotes/${quoteId}`);
      setQuote(data);
      setForm({
        title: data.title || "", client_id: data.client_id || "", client_name: data.client_name || "",
        client_email: data.client_email || "", client_phone: data.client_phone || "",
        client_company: data.client_company || "",
        contact_id: data.contact_id || "", contact_name: data.contact_name || "", contact_color: data.contact_color || "",
        property_name: data.property_name || "", street_address: data.street_address || "",
        town_city: data.town_city || "", county: data.county || "", postcode: data.postcode || "",
        property_status: data.property_status || "", tenant_name: data.tenant_name || "", tenant_phone: data.tenant_phone || "",
        site_contact_name: data.site_contact_name || "", site_contact_phone: data.site_contact_phone || "",
        markup_a_pct: data.markup_a_pct || 0, markup_b_pct: data.markup_b_pct || 0,
        markup_a_label: data.markup_a_label || "Our Mark Up", markup_b_label: data.markup_b_label || "Client Mark Up",
        line_items: data.line_items?.length ? data.line_items : [{ ...EMPTY_LINE, id: crypto.randomUUID() }],
        validity_days: data.validity_days || 30, terms: data.terms || "", notes: data.notes || "",
        scope_of_works: data.scope_of_works || "", caveats: data.caveats || "", notice: { ...EMPTY_NOTICE, ...(data.notice || {}) },
        vat_enabled: data.vat_enabled || false, vat_rate: data.vat_rate || 0,
        images: data.images || [],
        // Phase 291 — preserve supplier-source fields on existing quotes so
        // re-saving an old in-house quote never accidentally converts it.
        source_type: data.source_type || "in_house",
        vendor_id: data.vendor_id || "",
        vendor_name: data.vendor_name || "",
        vendor_email: data.vendor_email || "",
        vendor_phone: data.vendor_phone || "",
        vendor_company: data.vendor_company || "",
        create_supplier_record: false,  // never re-create on edit
      });
      // Load contacts for this client
      if (data.client_id) {
        try {
          const cr = await API.get(`/clients/${data.client_id}`);
          setContacts(cr.data.contacts || []);
        } catch {}
      }
    } catch { toast.error("Failed to load quote"); navigate("/quotes"); }
    finally { setLoading(false); }
  }, [quoteId, isNew, navigate]);

  useEffect(() => { fetchQuote(); }, [fetchQuote]);

  useEffect(() => {
    API.get("/clients?limit=500").then(({ data }) => setClients(data.clients || [])).catch(() => {});
    API.get("/markup-templates").then(({ data }) => setMarkupTemplates(data.templates || [])).catch(() => {});
    API.get("/quote-templates").then(({ data }) => setQuoteTemplates(data.templates || [])).catch(() => {});
    // Phase 291 — Fetch onboarded suppliers for the SupplierPicker so admins
    // can pin a supplier-source quote to a roster record (auto-fills vendor_*)
    // Note: GET /suppliers returns a BARE JSON array (not a `{vendors:[…]}`
    // envelope), so unwrap defensively — handle both shapes so future backend
    // refactors don't silently break this picker.
    API.get("/suppliers?limit=500").then(({ data }) => {
      const list = Array.isArray(data) ? data : (data.vendors || data.suppliers || []);
      setOnboardedSuppliers(list);
    }).catch(() => {});
    // Load quote-email feature flag (white-label opt-in)
    API.get("/settings/quote-email").then(({ data }) => setEmailEnabled(!!data.enabled)).catch(() => {});
    // Auto-load VAT settings for new quotes
    if (isNew) {
      API.get("/settings/vat").then(({ data }) => {
        if (data.vat_enabled) {
          setForm(f => ({ ...f, vat_enabled: true, vat_rate: data.vat_rate || 20 }));
        }
      }).catch(() => {});
    }
  }, [isNew]);

  // Phase 262 — Pre-fill a new quote from the linked Job Request
  useEffect(() => {
    if (!isNew || !jrIdParam) return;
    setLinkedJRId(jrIdParam);
    (async () => {
      try {
        const { data: jr } = await API.get(`/job-requests/${jrIdParam}`);
        setLinkedJR(jr);
        setForm(f => ({
          ...f,
          title: f.title || jr.title || "",
          client_id: jr.client_id || f.client_id,
          client_name: jr.client_name || f.client_name,
          client_email: jr.client_email || f.client_email,
          client_phone: jr.client_phone || f.client_phone,
          client_company: jr.client_company || f.client_company,
          contact_id: jr.requester_contact_id || f.contact_id,
          contact_name: jr.requester_contact_name || f.contact_name,
          contact_color: jr.requester_contact_color || f.contact_color,
          street_address: jr.property_address || f.street_address,
          scope_of_works: f.scope_of_works || jr.scope_of_works || "",
          caveats: f.caveats || jr.caveats || "",
          notice: f.notice?.text ? f.notice : noticeFromSource(jr.notice, f.notice),
          notes: f.notes || (jr.request_details ? `Linked Job Request: ${jr.reference}\n\n${jr.request_details}` : f.notes),
        }));
        setScopeFrom(copiedTerms(jr) ? { ref: jr.reference || "the request", fields: copiedTerms(jr) } : null);
        // Load contacts for the JR's client
        if (jr.client_id) {
          try {
            const cr = await API.get(`/clients/${jr.client_id}`);
            setContacts(cr.data.contacts || []);
          } catch {}
        }
      } catch (e) {
        toast.error("Could not load linked Job Request data");
      }
    })();
  }, [isNew, jrIdParam]);

  // Variation quote raised from a live job — prefill client/site from the job
  useEffect(() => {
    const jid = isNew ? jobIdParam : quote?.job_id;
    if (!jid) return;
    API.get(`/jobs/${jid}`).then(({ data: job }) => {
      setLinkedJob(job);
      if (!isNew) return;
      setForm(f => ({
        ...f,
        title: f.title || (job.parent_job_id || !(job.quotes || []).some(q => q.approved) ? `${job.title || job.reference}` : `Variation — ${job.title || job.reference}`),
        client_id: job.client_id || f.client_id, client_name: job.client_name || f.client_name, client_email: job.client_email || f.client_email,
        client_phone: job.client_phone || f.client_phone, client_company: job.client_company || f.client_company,
        contact_id: job.requester_contact_id || f.contact_id, contact_name: job.requester_contact_name || f.contact_name,
        property_name: job.property_name || f.property_name, street_address: job.street_address || f.street_address, town_city: job.town_city || f.town_city,
        county: job.county || f.county, postcode: job.postcode || f.postcode,
        scope_of_works: f.scope_of_works || job.scope_of_works || "",
        caveats: f.caveats || job.caveats || "",
        notice: f.notice?.text ? f.notice : noticeFromSource(job.notice, f.notice),
        notes: f.notes || (job.parent_job_id ? `Quote for sub-job ${job.reference}${job.parent_job_reference ? ` (main job ${job.parent_job_reference})` : ""}` : `Quote for job ${job.reference}${job.title ? ` — ${job.title}` : ""}`),
      }));
      setScopeFrom(copiedTerms(job) ? { ref: job.reference || "the job", fields: copiedTerms(job) } : null);
    }).catch(() => {});
  }, [isNew, jobIdParam, quote?.job_id]);

  // Track linked JR for existing in-house quotes
  useEffect(() => {
    if (isNew || !quote?.job_request_id) return;
    setLinkedJRId(quote.job_request_id);
    API.get(`/job-requests/${quote.job_request_id}`).then(({ data }) => setLinkedJR(data)).catch(() => {});
  }, [isNew, quote?.job_request_id]);

  const handleClientSelect = async (clientId) => {
    const client = clients.find(c => c.id === clientId);
    if (!client) return;
    setForm(f => ({
      ...f,
      client_id: client.id,
      client_name: `${client.first_name} ${client.last_name}`.trim(),
      client_email: client.email || "",
      client_phone: client.phone || "",
      client_company: client.company_name || "",
    }));
    setContacts(client.contacts || []);
  };

  const handleContactSelect = (contactId) => {
    const contact = contacts.find(c => c.id === contactId);
    if (!contact) return;
    setForm(f => ({
      ...f,
      contact_id: contact.id,
      contact_name: `${contact.first_name} ${contact.last_name}`.trim(),
      contact_color: contact.color || "",
    }));
  };

  const handleMarkupTemplate = (templateId) => {
    const tpl = markupTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    setForm(f => ({
      ...f,
      markup_a_pct: tpl.markup_a_pct, markup_b_pct: tpl.markup_b_pct,
      markup_a_label: tpl.markup_a_label, markup_b_label: tpl.markup_b_label,
    }));
  };

  const handleLoadQuoteTemplate = (templateId) => {
    const tpl = quoteTemplates.find(t => t.id === templateId);
    if (!tpl) return;
    const items = (tpl.line_items || []).map(li => ({ ...li, id: crypto.randomUUID() }));
    setForm(f => ({
      ...f,
      line_items: items.length ? items : f.line_items,
      terms: tpl.terms || f.terms,
      scope_of_works: tpl.scope_of_works || f.scope_of_works,
      notice: tpl.notice?.text ? { ...EMPTY_NOTICE, ...tpl.notice } : f.notice,
      caveats: tpl.caveats || f.caveats,
      validity_days: tpl.validity_days || f.validity_days,
      markup_a_pct: tpl.markup_a_pct || f.markup_a_pct,
      markup_b_pct: tpl.markup_b_pct || f.markup_b_pct,
      markup_a_label: tpl.markup_a_label || f.markup_a_label,
      markup_b_label: tpl.markup_b_label || f.markup_b_label,
    }));
    toast.success(`Loaded template: ${tpl.name}`);
  };

  const handleSaveAsTemplate = async () => {
    const name = window.prompt("Template name:", form.title || "My Template");
    if (!name) return;
    try {
      await API.post("/quote-templates", {
        name, line_items: form.line_items, terms: form.terms,
        scope_of_works: form.scope_of_works, caveats: form.caveats, notice: form.notice,
        validity_days: form.validity_days, markup_a_pct: form.markup_a_pct,
        markup_b_pct: form.markup_b_pct, markup_a_label: form.markup_a_label,
        markup_b_label: form.markup_b_label,
      });
      toast.success(`Template "${name}" saved`);
      API.get("/quote-templates").then(({ data }) => setQuoteTemplates(data.templates || [])).catch(() => {});
    } catch { toast.error("Failed to save template"); }
  };

  const imageInputRef = useRef(null);
  const [uploadingImage, setUploadingImage] = useState(false);

  const handleImageUpload = async (files) => {
    if (!files?.length) return;
    setUploadingImage(true);
    try {
      const newImages = [];
      for (const file of Array.from(files)) {
        const fd = new FormData();
        fd.append("file", file);
        const { data } = await API.post("/upload?entity_type=quote_image&entity_id=" + (quoteId || "new"), fd, { headers: { "Content-Type": "multipart/form-data" } });
        newImages.push({
          id: crypto.randomUUID(),
          file_id: data.file_id || data.id,
          file_name: data.file_name || file.name,
          file_url: data.file_url || `/api/files/${data.file_id}/${data.file_name}`,
          label: "",
          linked_item: "",
        });
      }
      setForm(f => ({ ...f, images: [...f.images, ...newImages] }));
      toast.success(`${newImages.length} image(s) uploaded`);
    } catch { toast.error("Failed to upload images"); }
    finally { setUploadingImage(false); }
  };

  const updateImage = (idx, field, value) => {
    setForm(f => {
      const imgs = [...f.images];
      imgs[idx] = { ...imgs[idx], [field]: value };
      return { ...f, images: imgs };
    });
  };

  const removeImage = (idx) => setForm(f => ({ ...f, images: f.images.filter((_, i) => i !== idx) }));

  // Line item helpers
  const updateLine = (idx, field, value) => {
    setForm(f => {
      const items = [...f.line_items];
      items[idx] = { ...items[idx], [field]: value };
      return { ...f, line_items: items };
    });
  };
  const addLine = () => setForm(f => ({ ...f, line_items: [...f.line_items, { ...EMPTY_LINE, id: crypto.randomUUID() }] }));
  const removeLine = (idx) => setForm(f => ({ ...f, line_items: f.line_items.filter((_, i) => i !== idx) }));

  // Calculate totals
  const calcLine = (line) => {
    const base = (parseFloat(line.quantity) || 0) * (parseFloat(line.unit_cost) || 0);
    const afterA = base * (1 + (parseFloat(form.markup_a_pct) || 0) / 100);
    const total = afterA * (1 + (parseFloat(form.markup_b_pct) || 0) / 100);
    return { base: Math.round(base * 100) / 100, afterA: Math.round(afterA * 100) / 100, total: Math.round(total * 100) / 100 };
  };
  const baseTotal = form.line_items.reduce((s, li) => s + calcLine(li).base, 0);
  const afterATotal = form.line_items.reduce((s, li) => s + calcLine(li).afterA, 0);
  const grandTotal = form.line_items.reduce((s, li) => s + calcLine(li).total, 0);
  const vatAmount = form.vat_enabled && form.vat_rate > 0 ? Math.round(grandTotal * (form.vat_rate / 100) * 100) / 100 : 0;
  const totalIncVat = Math.round((grandTotal + vatAmount) * 100) / 100;

  const handleSave = async () => {
    if (!form.title.trim()) { toast.error("Title is required"); return; }
    if (form.line_items.length === 0) { toast.error("Add at least one line item"); return; }
    // Phase 291 — supplier-source quotes need at least the supplier name so
    // the JR panel can render something meaningful. If admin ticked
    // create_supplier_record, we also require an email so the auto-created
    // supplier is invitable later.
    if (form.source_type === "supplier") {
      if (!form.vendor_name.trim() && !form.vendor_company.trim() && !form.vendor_id) {
        toast.error("Pick a supplier or fill in the supplier name/company");
        return;
      }
      if (form.create_supplier_record && !form.vendor_email.trim()) {
        toast.error("Supplier email is required to add them as a new record");
        return;
      }
    }
    setSaving(true);
    try {
      if (isNew) {
        const payload = { ...form };
        if (linkedJRId) payload.job_request_id = linkedJRId;
        if (jobIdParam) payload.job_id = jobIdParam;
        const { data } = await API.post("/quotes", payload);
        toast.success(`Quote ${data.reference} created`);
        if (linkedJRId) {
          // Return to the originating JR to keep the workflow tight
          navigate(`/job-requests/${linkedJRId}`);
        } else if (jobIdParam) {
          navigate(`/jobs/${jobIdParam}`);
        } else {
          navigate(`/quotes/${data.id}`);
        }
      } else {
        await API.put(`/quotes/${quoteId}`, form);
        toast.success("Quote updated");
        fetchQuote();
      }
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to save"); }
    finally { setSaving(false); }
  };

  const handleStatusChange = async (status) => {
    try {
      await API.put(`/quotes/${quoteId}/status`, { status });
      toast.success(`Status updated to ${status}`);
      fetchQuote();
    } catch { toast.error("Failed to update status"); }
  };

  const handleDuplicate = async () => {
    try {
      const { data } = await API.post(`/quotes/${quoteId}/duplicate`);
      toast.success(`Duplicated as ${data.reference}`);
      navigate(`/quotes/${data.id}`);
    } catch { toast.error("Failed to duplicate quote"); }
  };

  const handleAcceptConvert = async (withWO) => {
    setConverting(true);
    try {
      const { data } = await API.post(`/quotes/${quoteId}/convert-to-job`, { create_works_order: withWO });
      toast.success(data.message);
      if (data.works_order_id) toast.success(`Draft Works Order ${data.wo_number} created for this supplier`);
      navigate(`/jobs/${data.job_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Failed to accept quote"); }
    finally { setConverting(false); }
  };

  const handleConvert = async (type) => {
    setConverting(true);
    try {
      const endpoint = type === "job" ? `/quotes/${quoteId}/convert-to-job` : `/quotes/${quoteId}/convert-to-project`;
      const payload = type === "job" ? { create_works_order: convertCreateWO } : undefined;
      const { data } = await API.post(endpoint, payload);
      toast.success(data.message);
      if (data.works_order_id) {
        toast.success(`Draft Works Order ${data.wo_number} created`);
      }
      setShowConvert(false);
      fetchQuote();
      if (type === "job") navigate(`/jobs/${data.job_id}`);
      else navigate(`/projects/${data.project_id}`);
    } catch (err) { toast.error(err.response?.data?.detail || "Conversion failed"); }
    finally { setConverting(false); }
  };

  const handleDownloadPdf = async (view) => {
    try {
      const response = await API.get(`/quotes/${quoteId}/pdf?view=${view}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${quote?.reference || "quote"}_${view}.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`${view === "client" ? "Client" : "Admin"} PDF downloaded`);
    } catch { toast.error("Failed to download PDF"); }
  };

  const openBrandingDialog = async () => {
    // Start with empty form — user fills in what they need each time
    setBrandingForm({ company_name: "", tagline: "", logo_url: "", footer_text: "" });
    setBrandingLogoFile(null);
    // Load saved branding as a reference but don't auto-fill
    if (form.client_id) {
      try {
        const { data } = await API.get(`/clients/${form.client_id}/branding`);
        setSavedBranding(data);
      } catch {
        setSavedBranding(null);
      }
    } else {
      setSavedBranding(null);
    }
    setShowBrandingDialog(true);
  };

  const handleBrandingLogoUpload = async (file) => {
    if (!file) return;
    setBrandingUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await API.post("/upload?entity_type=branding&entity_id=logo", fd, { headers: { "Content-Type": "multipart/form-data" } });
      const logoUrl = data.file_url || `/api/files/${data.file_id}/${data.file_name}`;
      setBrandingForm(f => ({ ...f, logo_url: logoUrl }));
      toast.success("Logo uploaded");
    } catch { toast.error("Failed to upload logo"); }
    finally { setBrandingUploading(false); }
  };

  const handleBrandedPdfDownload = async () => {
    // Save branding for this client if client selected
    if (form.client_id && (brandingForm.company_name || brandingForm.logo_url)) {
      try { await API.put(`/clients/${form.client_id}/branding`, brandingForm); } catch {}
    }
    // Download branded PDF
    try {
      const response = await API.post(`/quotes/${quoteId}/pdf`, {
        view: "client", ...brandingForm,
      }, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([response.data], { type: "application/pdf" }));
      const link = document.createElement("a");
      link.href = url;
      link.download = `${quote?.reference || "quote"}_client.pdf`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Branded Client PDF downloaded");
      setShowBrandingDialog(false);
    } catch { toast.error("Failed to download PDF"); }
  };

  if (loading) return <div className="flex justify-center py-16"><div className="animate-spin h-6 w-6 border-2 border-primary border-t-transparent rounded-full" /></div>;

  return (
    <div data-testid="quote-detail-page">
      <div className="flex items-center gap-2 mb-4">
        <Link to="/quotes" className="text-sm text-muted-foreground hover:text-foreground flex items-center gap-1">
          <ArrowLeft size={14} /> Back to Quotes
        </Link>
        {quote && <Badge className={`ml-2 text-[10px] ${STATUS_STYLES[quote.status] || ""}`}>{quote.status?.toUpperCase()}</Badge>}
      </div>

      {/* Phase 262 — Linked Job Request banner */}
      {linkedJRId && linkedJR && (
        <Link
          to={`/job-requests/${linkedJRId}`}
          className="mb-4 flex items-center gap-2 p-2.5 border border-amber-200 dark:border-amber-800/50 bg-amber-50 dark:bg-amber-900/20 rounded-md text-xs hover:bg-amber-100 transition-colors"
          data-testid="quote-linked-jr-banner"
        >
          <Receipt size={14} className="text-amber-700" />
          <span className="text-amber-800 dark:text-amber-200">
            In-house quote for Job Request <strong>{linkedJR.reference}</strong> — {linkedJR.title}
          </span>
          <ArrowLeft size={11} className="ml-auto rotate-180 text-amber-700" />
        </Link>
      )}

      <div className="flex items-center justify-between mb-6">
        <div className="flex items-center gap-3">
          <div className="h-9 w-9 rounded-lg bg-gradient-to-br from-teal-500 to-cyan-600 flex items-center justify-center">
            <Receipt size={18} className="text-white" weight="fill" />
          </div>
          <div>
            <div className="flex items-center gap-2 flex-wrap">
              <h1 className="text-xl font-bold font-[Chivo] tracking-tight">{isNew ? (form.source_type === "supplier" ? "New Supplier Quote" : "New Quote") : quote?.reference}</h1>
              {form.source_type === "supplier" && (
                <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]" data-testid="quote-source-badge">
                  <Briefcase size={10} className="mr-1" /> Supplier
                </Badge>
              )}
              {quote?.source === "vendor_portal" && (
                <Badge className="bg-indigo-100 text-indigo-700 dark:bg-indigo-900/40 dark:text-indigo-300 text-[10px]" data-testid="quote-vendor-portal-badge">
                  <Briefcase size={10} className="mr-1" /> Supplier Portal
                </Badge>
              )}
              {(quote?.job_id || jobIdParam) && (
                <Badge className="bg-amber-100 text-amber-800 dark:bg-amber-900/40 dark:text-amber-200 text-[10px]" data-testid="quote-variation-badge">
                  Variation{linkedJob ? ` · ${linkedJob.reference}` : ""}
                </Badge>
              )}
              {quote?.portal_sent_at && quote?.status === "sent" && (
                <PortalLiveBadge pushedAt={quote.portal_sent_at} notified={quote.portal_notified} withdrawUrl={`/quotes/${quoteId}/withdraw-from-portal`} onDone={fetchQuote} testId="quote-portal-live" />
              )}
            </div>
            {quote && <p className="text-xs text-muted-foreground">{fmtDate(quote.created_at)}</p>}
          </div>
        </div>
        {!isNew && quote?.status !== "converted" && (
          <div className="flex items-center gap-2">
            {quote?.source === "vendor_portal" && quote?.status === "submitted" && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" disabled={converting} onClick={() => handleAcceptConvert(true)} data-testid="accept-convert-wo-btn">
                  {converting ? "Accepting..." : "Accept → Job + Works Order"}
                </Button>
                <Button size="sm" variant="outline" className="text-emerald-700 border-emerald-300" disabled={converting} onClick={() => handleAcceptConvert(false)} data-testid="accept-convert-job-btn">
                  Accept → Job only
                </Button>
              </>
            )}
            {!quote?.portal_sent_at && !quote?.client_response && ["draft", "sent", "approved", "declined", "expired"].includes(quote?.status) && (
              <PortalPushButton url={`/quotes/${quoteId}/send-to-portal`} amount={quote?.grand_total} onDone={fetchQuote} testId="quote-send-to-portal-btn"
                disabledReason={!quote?.client_id ? "Attach this quote to a client first" : ""} />
            )}
            {quote?.status === "draft" && <Button size="sm" variant="outline" onClick={() => handleStatusChange("sent")} data-testid="mark-sent-btn">Mark as Sent</Button>}
            {quote?.status === "sent" && (
              <>
                <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700 text-white" onClick={() => handleStatusChange("approved")} data-testid="mark-approved-btn">Approve</Button>
                <Button size="sm" variant="outline" className="text-red-600 border-red-200" onClick={() => handleStatusChange("declined")} data-testid="mark-declined-btn">Decline</Button>
              </>
            )}
            {["draft", "sent", "approved"].includes(quote?.status) && (
              <Button size="sm" onClick={() => setShowConvert(true)} data-testid="convert-btn">
                Convert <ArrowLeft size={12} className="ml-1 rotate-180" />
              </Button>
            )}
            <Button size="sm" variant="outline" onClick={openBrandingDialog} data-testid="pdf-client-btn">
              <FilePdf size={14} className="mr-1" /> Client PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleDownloadPdf("admin")} data-testid="pdf-admin-btn">
              <FilePdf size={14} className="mr-1" /> Admin PDF
            </Button>
            <Button size="sm" variant="outline" onClick={handleDuplicate} data-testid="duplicate-quote-btn">
              <Copy size={14} className="mr-1" /> Duplicate
            </Button>
            {emailEnabled && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowEmailDialog(true)} data-testid="email-quote-btn">
                <EnvelopeSimple size={14} className="mr-1" /> Email Quote
              </Button>
            )}
            <QuoteApprovalLinkButton quote={quote} onSent={fetchQuote} />
          </div>
        )}
        {!isNew && quote?.status === "converted" && (
          <div className="flex items-center gap-2">
            {quote.converted_to && (
              <Link to={quote.converted_to.type === "job" ? `/jobs/${quote.converted_to.id}` : `/projects/${quote.converted_to.id}`}>
                <Button size="sm" variant="outline" data-testid="view-converted-btn">
                  View {quote.converted_to.type === "job" ? "Job" : "Project"}: {quote.converted_to.reference}
                </Button>
              </Link>
            )}
            <Button size="sm" variant="outline" onClick={openBrandingDialog} data-testid="pdf-client-btn">
              <FilePdf size={14} className="mr-1" /> Client PDF
            </Button>
            <Button size="sm" variant="outline" onClick={() => handleDownloadPdf("admin")} data-testid="pdf-admin-btn">
              <FilePdf size={14} className="mr-1" /> Admin PDF
            </Button>
            <Button size="sm" variant="outline" onClick={handleDuplicate} data-testid="duplicate-quote-btn-converted">
              <Copy size={14} className="mr-1" /> Duplicate
            </Button>
            {emailEnabled && (
              <Button size="sm" className="bg-blue-600 hover:bg-blue-700 text-white" onClick={() => setShowEmailDialog(true)} data-testid="email-quote-btn">
                <EnvelopeSimple size={14} className="mr-1" /> Email Quote
              </Button>
            )}
            <Button size="sm" variant="outline" className="text-amber-600 border-amber-300 hover:bg-amber-50 dark:hover:bg-amber-950/30" onClick={() => setShowUnconvert(true)} data-testid="unconvert-btn">
              <ArrowCounterClockwise size={14} className="mr-1" /> Undo Conversion
            </Button>
          </div>
        )}
      </div>

      {!isNew && quote?.scope_drift && <ScopeDriftBanner quote={quote} onResolved={fetchQuote} />}

      {!isNew && quote?.client_response?.via === "client_portal" && (
        <div className="mb-6">
          <ClientDecisionBanner response={quote.client_response} amount={quote.grand_total} testId="quote-client-decision"
            convertLabel={quote.job_id ? null : "Convert to job"}
            onConvert={quote.status === "approved" && !quote.job_id ? () => setShowConvert(true) : null} />
        </div>
      )}

      <Dialog open={showUnconvert} onOpenChange={setShowUnconvert}>
        <DialogContent className="max-w-md" data-testid="unconvert-dialog">
          <DialogHeader><DialogTitle>Undo conversion?</DialogTitle></DialogHeader>
          <p className="text-sm text-muted-foreground">
            This will <strong>delete {quote?.converted_to?.type === "job" ? "Job" : "Project"} {quote?.converted_to?.reference}</strong>
            {quote?.converted_to?.type === "job" ? " (and any draft works orders created with it)" : " (and its unstarted tasks)"} and
            restore this quote so you can convert it again — for example to a {quote?.converted_to?.type === "job" ? "project" : "job"} instead.
          </p>
          <p className="text-xs text-muted-foreground">
            Undo is blocked automatically if the {quote?.converted_to?.type === "job" ? "job" : "project"} has already progressed, been assigned, or invoiced.
          </p>
          <DialogFooter>
            <Button variant="ghost" onClick={() => setShowUnconvert(false)} disabled={unconverting} data-testid="unconvert-cancel-btn">Cancel</Button>
            <Button className="bg-amber-600 hover:bg-amber-700 text-white" onClick={handleUnconvert} disabled={unconverting} data-testid="unconvert-confirm-btn">
              {unconverting ? "Undoing…" : "Yes, Undo Conversion"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Form */}
      <div className="space-y-6">
        {/* Title */}
        <div>
          <Label className="text-xs font-semibold">Quote Title *</Label>
          <Input value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Kitchen Renovation - 12 High St" className="mt-1" data-testid="quote-title" />
        </div>

        {/* Phase 291 — Supplier Details panel (only shown for supplier-source
            quotes so the in-house flow is completely unchanged). Sits at the
            top of the form because who's quoting is the most important thing
            for a supplier quote. */}
        {form.source_type === "supplier" && (
          <div className="border border-blue-200 dark:border-blue-800/60 bg-blue-50/40 dark:bg-blue-900/10 rounded-lg p-4 space-y-3" data-testid="quote-supplier-panel">
            <div className="flex items-center justify-between gap-2 flex-wrap">
              <h3 className="text-sm font-semibold font-[Chivo] text-blue-900 dark:text-blue-200">
                <Briefcase size={14} className="inline mr-1.5 mb-0.5" /> Supplier Details
              </h3>
              <Badge className="bg-blue-100 text-blue-700 dark:bg-blue-900/40 dark:text-blue-300 text-[10px]">Supplier Quote</Badge>
            </div>
            <SupplierPicker
              suppliers={onboardedSuppliers}
              selectedId={form.vendor_id}
              onPick={(supplier) => {
                if (!supplier) {
                  setForm((f) => ({ ...f, vendor_id: "", create_supplier_record: false }));
                  return;
                }
                setForm((f) => ({
                  ...f,
                  vendor_id: supplier.id || "",
                  vendor_name: supplier.contact_name || supplier.name || f.vendor_name,
                  vendor_email: supplier.email || f.vendor_email,
                  vendor_phone: supplier.phone || f.vendor_phone,
                  vendor_company: supplier.company_name || supplier.name || f.vendor_company,
                  create_supplier_record: false,  // linked to existing → nothing to auto-create
                }));
              }}
            />
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <div>
                <Label className="text-xs">Supplier Contact Name{form.vendor_id ? "" : " *"}</Label>
                <Input
                  value={form.vendor_name}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_name: e.target.value }))}
                  placeholder="e.g. John Smith"
                  data-testid="quote-vendor-name"
                />
              </div>
              <div>
                <Label className="text-xs">Supplier Company</Label>
                <Input
                  value={form.vendor_company}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_company: e.target.value }))}
                  placeholder="e.g. Smith Electrical Ltd"
                  data-testid="quote-vendor-company"
                />
              </div>
              <div>
                <Label className="text-xs">Supplier Email</Label>
                <Input
                  type="email"
                  value={form.vendor_email}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_email: e.target.value }))}
                  placeholder="john@smithelectrical.co.uk"
                  data-testid="quote-vendor-email"
                />
              </div>
              <div>
                <Label className="text-xs">Supplier Phone</Label>
                <Input
                  value={form.vendor_phone}
                  onChange={(e) => setForm((f) => ({ ...f, vendor_phone: e.target.value }))}
                  data-testid="quote-vendor-phone"
                />
              </div>
            </div>
            {/* Only show the "add to roster" toggle when NO existing supplier
                is linked — otherwise it'd be a no-op. */}
            {!form.vendor_id && isNew && (
              <label className="flex items-start gap-2 pt-1 cursor-pointer" data-testid="quote-vendor-create-toggle">
                <Checkbox
                  checked={form.create_supplier_record}
                  onCheckedChange={(v) => setForm((f) => ({ ...f, create_supplier_record: !!v }))}
                  className="mt-0.5"
                />
                <div className="text-xs">
                  <p className="font-medium text-slate-700 dark:text-slate-200">Add this supplier to my roster</p>
                  <p className="text-[10px] text-muted-foreground">
                    We'll create a supplier record so you can invite them, track their compliance, and pick them from future quote forms.
                  </p>
                </div>
              </label>
            )}
          </div>
        )}

        {/* Client & Contact */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold font-[Chivo]">Client Details</h3>
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div>
              <Label className="text-xs">Client *</Label>
              <Select value={form.client_id} onValueChange={handleClientSelect}>
                <SelectTrigger data-testid="quote-client-select"><SelectValue placeholder="Select client" /></SelectTrigger>
                <SelectContent>
                  {clients.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.company_name ? `(${c.company_name})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Sub-Contact</Label>
              <Select value={form.contact_id} onValueChange={handleContactSelect}>
                <SelectTrigger data-testid="quote-contact-select"><SelectValue placeholder="Select contact" /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="none">— None —</SelectItem>
                  {contacts.map(c => <SelectItem key={c.id} value={c.id}>{c.first_name} {c.last_name} {c.job_role ? `(${c.job_role})` : ""}</SelectItem>)}
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs">Client Email</Label>
              <Input value={form.client_email} onChange={e => setForm(f => ({ ...f, client_email: e.target.value }))} data-testid="quote-client-email" />
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Client Phone</Label><Input value={form.client_phone} onChange={e => setForm(f => ({ ...f, client_phone: e.target.value }))} data-testid="quote-client-phone" /></div>
            <div><Label className="text-xs">Company</Label><Input value={form.client_company} onChange={e => setForm(f => ({ ...f, client_company: e.target.value }))} data-testid="quote-client-company" /></div>
          </div>
        </div>

        {/* Property */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <h3 className="text-sm font-semibold font-[Chivo]">Property Details</h3>
          <AddressAutocomplete
            label="Search address"
            placeholder="Start typing address or postcode..."
            onSelect={(a) => setForm(f => ({
              ...f,
              street_address: a.street_address || f.street_address,
              town_city: a.town_city || f.town_city,
              county: a.county || f.county,
              postcode: a.postcode || f.postcode,
              latitude: a.latitude || null,
              longitude: a.longitude || null,
            }))}
            data-testid="quote-address-autocomplete"
          />
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
            <div><Label className="text-xs">Property Name</Label><Input value={form.property_name} onChange={e => setForm(f => ({ ...f, property_name: e.target.value }))} placeholder="e.g. Maple House" data-testid="quote-prop-name" /></div>
            <div className="sm:col-span-2"><Label className="text-xs">Street Address *</Label><Input value={form.street_address} onChange={e => setForm(f => ({ ...f, street_address: e.target.value }))} data-testid="quote-street" /></div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Town/City *</Label><Input value={form.town_city} onChange={e => setForm(f => ({ ...f, town_city: e.target.value }))} data-testid="quote-city" /></div>
            <div><Label className="text-xs">County</Label><Input value={form.county} onChange={e => setForm(f => ({ ...f, county: e.target.value }))} data-testid="quote-county" /></div>
            <div><Label className="text-xs">Postcode *</Label><Input value={form.postcode} onChange={e => setForm(f => ({ ...f, postcode: e.target.value }))} data-testid="quote-postcode" /></div>
            <div>
              <Label className="text-xs">Property Status</Label>
              <Select value={form.property_status} onValueChange={v => setForm(f => ({ ...f, property_status: v }))}>
                <SelectTrigger data-testid="quote-prop-status"><SelectValue placeholder="Select" /></SelectTrigger>
                <SelectContent>{PROPERTY_STATUSES.map(s => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Tenant Name</Label><Input value={form.tenant_name} onChange={e => setForm(f => ({ ...f, tenant_name: e.target.value }))} data-testid="quote-tenant-name" /></div>
            <div><Label className="text-xs">Tenant Phone</Label><Input value={form.tenant_phone} onChange={e => setForm(f => ({ ...f, tenant_phone: e.target.value }))} data-testid="quote-tenant-phone" /></div>
            <div><Label className="text-xs">Site Contact</Label><Input value={form.site_contact_name} onChange={e => setForm(f => ({ ...f, site_contact_name: e.target.value }))} data-testid="quote-site-contact" /></div>
            <div><Label className="text-xs">Site Phone</Label><Input value={form.site_contact_phone} onChange={e => setForm(f => ({ ...f, site_contact_phone: e.target.value }))} data-testid="quote-site-phone" /></div>
          </div>
        </div>

        {/* Markup Settings */}
        <div className="border border-border rounded-lg p-4 space-y-3">
          <div className="flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold font-[Chivo]">Markup Settings</h3>
            <div className="flex items-center gap-2">
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="h-8 text-xs"
                onClick={() => setReverseCalcOpen(true)}
                data-testid="quote-reverse-calc-btn"
              >
                <ArrowsClockwise size={12} className="mr-1" /> Reverse calc
              </Button>
              {markupTemplates.length > 0 && (
                <Select onValueChange={handleMarkupTemplate}>
                  <SelectTrigger className="w-48 h-8 text-xs" data-testid="markup-template-select"><SelectValue placeholder="Load template..." /></SelectTrigger>
                  <SelectContent>{markupTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({t.markup_a_pct}% / {t.markup_b_pct}%)</SelectItem>)}</SelectContent>
                </Select>
              )}
            </div>
          </div>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            <div><Label className="text-xs">Markup A Label</Label><Input value={form.markup_a_label} onChange={e => setForm(f => ({ ...f, markup_a_label: e.target.value }))} data-testid="markup-a-label" /></div>
            <div><Label className="text-xs">Markup A %</Label><Input type="number" step="0.1" value={form.markup_a_pct} onChange={e => setForm(f => ({ ...f, markup_a_pct: parseFloat(e.target.value) || 0 }))} data-testid="markup-a-pct" /></div>
            <div><Label className="text-xs">Markup B Label</Label><Input value={form.markup_b_label} onChange={e => setForm(f => ({ ...f, markup_b_label: e.target.value }))} data-testid="markup-b-label" /></div>
            <div><Label className="text-xs">Markup B %</Label><Input type="number" step="0.1" value={form.markup_b_pct} onChange={e => setForm(f => ({ ...f, markup_b_pct: parseFloat(e.target.value) || 0 }))} data-testid="markup-b-pct" /></div>
          </div>
        </div>

        {/* Line Items */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between gap-2 flex-wrap">
            <h3 className="text-sm font-semibold font-[Chivo]">Schedule of Works</h3>
            <div className="flex items-center gap-2">
              {quoteTemplates.length > 0 && (
                <Select onValueChange={handleLoadQuoteTemplate}>
                  <SelectTrigger className="w-44 h-8 text-xs" data-testid="load-quote-template"><SelectValue placeholder="Load template..." /></SelectTrigger>
                  <SelectContent>{quoteTemplates.map(t => <SelectItem key={t.id} value={t.id}>{t.name} ({(t.line_items||[]).length} items)</SelectItem>)}</SelectContent>
                </Select>
              )}
              <Button size="sm" variant="ghost" className="text-xs h-8" onClick={handleSaveAsTemplate} data-testid="save-as-template-btn">
                Save as Template
              </Button>
              <Button size="sm" variant="outline" onClick={addLine} data-testid="add-line-btn">
                <Plus size={14} className="mr-1" /> Add Item
              </Button>
            </div>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-xs" data-testid="line-items-table">
              <thead>
                <tr className="border-b border-border bg-muted/10">
                  <th className="px-3 py-2 text-left font-semibold w-8">#</th>
                  <th className="px-3 py-2 text-left font-semibold w-36">Item</th>
                  <th className="px-3 py-2 text-left font-semibold">Description</th>
                  <th className="px-3 py-2 text-right font-semibold w-16">Qty</th>
                  <th className="px-3 py-2 text-right font-semibold w-24">Unit Cost</th>
                  <th className="px-3 py-2 text-right font-semibold w-24">Sub Total</th>
                  {form.markup_a_pct > 0 && <th className="px-3 py-2 text-right font-semibold w-24">{form.markup_a_label}</th>}
                  <th className="px-3 py-2 text-right font-semibold w-24">Sub Total</th>
                  <th className="px-3 py-2 w-10"></th>
                </tr>
              </thead>
              <tbody>
                {form.line_items.map((line, idx) => {
                  const calc = calcLine(line);
                  return (
                    <tr key={line.id || idx} className="border-b border-border align-top" data-testid={`line-${idx}`}>
                      <td className="px-3 py-2 text-muted-foreground pt-3">{idx + 1}</td>
                      <td className="px-3 py-1">
                        <Input value={line.item_name || ""} onChange={e => updateLine(idx, "item_name", e.target.value)} className="h-8 text-xs border-0 shadow-none focus-visible:ring-1" placeholder="Item name" data-testid={`line-item-${idx}`} />
                      </td>
                      <td className="px-3 py-1">
                        <Textarea
                          value={line.description}
                          onChange={e => updateLine(idx, "description", e.target.value)}
                          rows={Math.max(1, (line.description || "").split("\n").length)}
                          className="min-h-[2rem] text-xs border-0 shadow-none focus-visible:ring-1 resize-y py-1.5 leading-snug"
                          placeholder="Description (Shift+Enter for new line)"
                          data-testid={`line-desc-${idx}`}
                        />
                      </td>
                      <td className="px-3 py-1">
                        <input value={line.quantity} onChange={e => updateLine(idx, "quantity", e.target.value)} className="h-8 w-16 text-xs text-right bg-transparent outline-none focus:ring-1 focus:ring-primary rounded px-2" placeholder="1" data-testid={`line-qty-${idx}`} />
                      </td>
                      <td className="px-3 py-1">
                        <input value={line.unit_cost} onChange={e => updateLine(idx, "unit_cost", e.target.value)} className="h-8 w-24 text-xs text-right bg-transparent outline-none focus:ring-1 focus:ring-primary rounded px-2" placeholder="0.00" data-testid={`line-cost-${idx}`} />
                      </td>
                      <td className="px-3 py-2 text-right font-mono">&pound;{calc.base.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      {form.markup_a_pct > 0 && <td className="px-3 py-2 text-right font-mono">&pound;{calc.afterA.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>}
                      <td className="px-3 py-2 text-right font-mono font-semibold">&pound;{calc.total.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td className="px-3 py-2">
                        {form.line_items.length > 1 && (
                          <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => removeLine(idx)} data-testid={`remove-line-${idx}`}>
                            <Trash size={12} className="text-muted-foreground" />
                          </Button>
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="bg-muted/20 border-t-2 border-border">
                  <td colSpan={4}></td>
                  <td className="px-3 py-2.5 text-right text-xs font-semibold">Sub Totals:</td>
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="base-total">&pound;{baseTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  {form.markup_a_pct > 0 && <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="after-a-total">&pound;{afterATotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>}
                  <td className="px-3 py-2.5 text-right font-mono font-semibold text-sm" data-testid="grand-total">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  <td></td>
                </tr>
                {form.vat_enabled && form.vat_rate > 0 && (
                  <>
                    <tr>
                      <td colSpan={4}></td>
                      <td className="px-3 py-1.5 text-right text-xs text-muted-foreground">VAT ({form.vat_rate}%):</td>
                      <td colSpan={form.markup_a_pct > 0 ? 2 : 1}></td>
                      <td className="px-3 py-1.5 text-right font-mono text-sm" data-testid="vat-amount">&pound;{vatAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                    <tr className="bg-primary/5">
                      <td colSpan={4}></td>
                      <td className="px-3 py-2.5 text-right text-xs font-bold">Total (inc. VAT):</td>
                      <td colSpan={form.markup_a_pct > 0 ? 2 : 1}></td>
                      <td className="px-3 py-2.5 text-right font-mono font-bold text-base text-primary" data-testid="total-inc-vat">&pound;{totalIncVat.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      <td></td>
                    </tr>
                  </>
                )}
              </tfoot>
            </table>
          </div>
        </div>

        {/* Cost Breakdown Box */}
        {(form.markup_a_pct > 0 || form.markup_b_pct > 0 || (form.vat_enabled && form.vat_rate > 0)) && (
          <div className="border border-border rounded-lg overflow-hidden" data-testid="cost-breakdown">
            <div className="px-4 py-3 bg-muted/30 border-b border-border">
              <h3 className="text-sm font-semibold font-[Chivo]">Cost Breakdown</h3>
            </div>
            <div className="p-4">
              <table className="w-full max-w-md text-sm">
                <tbody>
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 text-muted-foreground">Sub Total</td>
                    <td className="py-2.5 text-right font-mono font-medium">&pound;{baseTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {form.markup_a_pct > 0 && (
                    <>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">{form.markup_a_label} ({form.markup_a_pct}%)</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{(afterATotal - baseTotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">After {form.markup_a_label}</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{afterATotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  )}
                  {form.markup_b_pct > 0 && (
                    <tr className="border-b border-border/50">
                      <td className="py-2.5 text-muted-foreground">{form.markup_b_label} ({form.markup_b_pct}%)</td>
                      <td className="py-2.5 text-right font-mono font-medium">&pound;{(grandTotal - afterATotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  <tr className="border-b border-border/50">
                    <td className="py-2.5 font-medium text-foreground">Sub Total (excl. VAT)</td>
                    <td className="py-2.5 text-right font-mono font-semibold">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                  {form.vat_enabled && form.vat_rate > 0 && (
                    <>
                      <tr className="border-b border-border/50">
                        <td className="py-2.5 text-muted-foreground">VAT ({form.vat_rate}%)</td>
                        <td className="py-2.5 text-right font-mono font-medium">&pound;{vatAmount.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                      <tr className="bg-primary/5 rounded">
                        <td className="py-3 font-bold text-foreground">Total (inc. VAT)</td>
                        <td className="py-3 text-right font-mono font-bold text-lg text-primary">&pound;{totalIncVat.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                      </tr>
                    </>
                  )}
                  {!(form.vat_enabled && form.vat_rate > 0) && (
                    <tr className="bg-primary/5 rounded">
                      <td className="py-3 font-semibold text-foreground">Grand Total</td>
                      <td className="py-3 text-right font-mono font-bold text-lg text-primary">&pound;{grandTotal.toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                    </tr>
                  )}
                  <tr>
                    <td className="pt-2 text-xs text-emerald-600 font-medium">Total Profit</td>
                    <td className="pt-2 text-right font-mono text-sm font-semibold text-emerald-600">&pound;{(grandTotal - baseTotal).toLocaleString("en-GB", { minimumFractionDigits: 2 })}</td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Scope of Works & Caveats — print as cards under the schedule on the quote (hidden when empty) */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4" data-testid="quote-scope-caveats">
          <div className="border border-border rounded-lg overflow-hidden border-l-4 border-l-primary">
            <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold font-[Chivo]">Scope of Works</h3>
                <p className="text-[11px] text-muted-foreground">Appears as a box under the quotation lines. Start a line with "-" for a bullet. Right-click a line to save it as a standard. Left blank = not shown.</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <ScopeSetPicker scope={form.scope_of_works} caveats={form.caveats} notice={form.notice} category={libraryCategory} onApply={(v) => setForm(f => ({ ...f, scope_of_works: v.scope_of_works, caveats: v.caveats, ...(v.notice ? { notice: { ...EMPTY_NOTICE, ...v.notice } } : {}) }))} testid="quote-scope-sets" compact />
                <CaveatLibraryPicker kind="scope" value={form.scope_of_works} onPick={(t) => setForm(f => ({ ...f, scope_of_works: appendCaveat(f.scope_of_works, t) }))} category={libraryCategory} testid="quote-scope-library" />
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" onClick={() => setAiKind("scope")} data-testid="ai-scope-btn">
                  <Sparkle size={13} weight="fill" className="mr-1" /> AI draft
                </Button>
              </div>
            </div>
            <div className="p-4">
              <Textarea value={form.scope_of_works} onChange={e => setForm(f => ({ ...f, scope_of_works: e.target.value }))} rows={6}
                        placeholder={"Describe what the works include, e.g.\n- Strip out existing kitchen units and worktops\n- Supply and fit new units, worktops and appliances\n- Make good and leave site clean"} className="text-xs" data-testid="quote-scope-of-works" onContextMenu={saveLineHandler(form.scope_of_works, setScopeLineMenu)} />
              <SaveLineMenu kind="scope" menu={scopeLineMenu} category={libraryCategory} onClose={() => setScopeLineMenu(null)} />
              {isNew && scopeFrom && (
                <p className="text-[11px] text-muted-foreground mt-2" data-testid="quote-scope-copied-from">{scopeFrom.fields} copied from {scopeFrom.ref} — edit here if this quote covers something different.</p>
              )}
            </div>
          </div>
          <div className="border border-border rounded-lg overflow-hidden border-l-4 border-l-amber-500">
            <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-start justify-between gap-3">
              <div>
                <h3 className="text-sm font-semibold font-[Chivo]">Caveats</h3>
                <p className="text-[11px] text-muted-foreground">Exclusions and assumptions — shown in an amber box beneath the scope. Right-click a line to save it as a standard. Left blank = not shown.</p>
              </div>
              <div className="flex items-center gap-1.5 shrink-0">
                <CaveatLibraryPicker value={form.caveats} onPick={(t) => setForm(f => ({ ...f, caveats: appendCaveat(f.caveats, t) }))} category={libraryCategory} testid="quote-caveats-library" />
                <Button size="sm" variant="outline" className="h-7 text-xs shrink-0 border-indigo-300 text-indigo-700 hover:bg-indigo-50 dark:border-indigo-700 dark:text-indigo-300 dark:hover:bg-indigo-950/40" onClick={() => setAiKind("caveats")} data-testid="ai-caveats-btn">
                  <Sparkle size={13} weight="fill" className="mr-1" /> {form.caveats.trim() ? "AI refine" : "AI draft"}
                </Button>
              </div>
            </div>
            <div className="p-4">
              <Textarea value={form.caveats} onChange={e => setForm(f => ({ ...f, caveats: e.target.value }))} rows={6}
                        placeholder={"e.g.\n- Asbestos survey excluded; any discovery quoted separately\n- Price assumes clear access 8am–5pm weekdays\n- Decoration not included unless listed"} className="text-xs" data-testid="quote-caveats" onContextMenu={saveLineHandler(form.caveats, setCaveatLineMenu)} />
              <SaveLineMenu kind="caveat" menu={caveatLineMenu} category={libraryCategory} onClose={() => setCaveatLineMenu(null)} />
            </div>
          </div>
          <div className="border border-border rounded-lg overflow-hidden lg:col-span-2" data-testid="quote-notice-block">
            <div className="px-4 py-3 bg-muted/30 border-b border-border">
              <h3 className="text-sm font-semibold font-[Chivo]">Notice</h3>
              <p className="text-[11px] text-muted-foreground">Red = urgent / health &amp; safety (printed above the schedule so it's read first). Amber = advice, Green = positive (printed under the caveats). Left blank = not shown.</p>
            </div>
            <div className="p-4">
              <NoticeEditor value={form.notice} onChange={(n) => setForm(f => ({ ...f, notice: n }))} testid="quote-notice" aiContext={{ entity: "quote", title: form.title, description: form.scope_of_works }} />
            </div>
          </div>
        </div>

        {/* Supporting Images */}
        <div className="border border-border rounded-lg overflow-hidden">
          <div className="px-4 py-3 bg-muted/30 border-b border-border flex items-center justify-between">
            <h3 className="text-sm font-semibold font-[Chivo]">Supporting Images</h3>
            <Button size="sm" variant="outline" onClick={() => imageInputRef.current?.click()} disabled={uploadingImage} data-testid="upload-images-btn">
              <UploadSimple size={14} className="mr-1" /> {uploadingImage ? "Uploading..." : "Upload Images"}
            </Button>
            <input ref={imageInputRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.heic" multiple onChange={e => handleImageUpload(e.target.files)} data-testid="image-file-input" />
          </div>
          {form.images.length === 0 ? (
            <div className="p-6 text-center text-muted-foreground text-xs cursor-pointer hover:bg-muted/10 transition-colors" onClick={() => imageInputRef.current?.click()}>
              Click to upload photos to support this quote (before/after, site photos, damage, etc.)
            </div>
          ) : (
            <div className="p-4 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {form.images.map((img, idx) => (
                <div key={img.id || idx} className="border border-border rounded-lg overflow-hidden bg-muted/10" data-testid={`quote-image-${idx}`}>
                  <div className="aspect-video bg-muted flex items-center justify-center overflow-hidden">
                    <img
                      src={img.file_url?.startsWith("http") ? img.file_url : `${process.env.REACT_APP_BACKEND_URL}${img.file_url}`}
                      alt={img.label || "Quote image"}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-2 space-y-1.5">
                    <Input
                      value={img.label || ""}
                      onChange={e => updateImage(idx, "label", e.target.value)}
                      placeholder="Image label (e.g. Damaged boiler)"
                      className="h-7 text-xs"
                      data-testid={`image-label-${idx}`}
                    />
                    <Select value={img.linked_item || "none"} onValueChange={v => updateImage(idx, "linked_item", v === "none" ? "" : v)}>
                      <SelectTrigger className="h-7 text-xs" data-testid={`image-link-${idx}`}><SelectValue placeholder="Link to item..." /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="none">No linked item</SelectItem>
                        {form.line_items.map((li, i) => (
                          <SelectItem key={li.id || i} value={li.item_name || li.description || `Item ${i + 1}`}>
                            {li.item_name || li.description || `Item ${i + 1}`}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <Button variant="ghost" size="sm" className="h-6 text-[10px] text-destructive w-full" onClick={() => removeImage(idx)} data-testid={`remove-image-${idx}`}>
                      <Trash size={12} className="mr-1" /> Remove
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Terms & Validity */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <div className="border border-border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold font-[Chivo]">Terms & Conditions</h3>
            <Textarea value={form.terms} onChange={e => setForm(f => ({ ...f, terms: e.target.value }))} rows={4} placeholder="Payment terms, caveats, conditions..." className="text-xs" data-testid="quote-terms" />
          </div>
          <div className="border border-border rounded-lg p-4 space-y-3">
            <h3 className="text-sm font-semibold font-[Chivo]">Additional Info</h3>
            <div>
              <Label className="text-xs">Validity (days)</Label>
              <Input type="number" value={form.validity_days} onChange={e => setForm(f => ({ ...f, validity_days: parseInt(e.target.value) || 30 }))} className="w-24 mt-1" data-testid="quote-validity" />
            </div>
            <div>
              <Label className="text-xs">Internal Notes</Label>
              <Textarea value={form.notes} onChange={e => setForm(f => ({ ...f, notes: e.target.value }))} rows={2} placeholder="Internal notes (not shown on client PDF)" className="text-xs mt-1" data-testid="quote-notes" />
            </div>
          </div>
        </div>

        {/* Team Notes */}
        {!isNew && quote && (
          <div className="border border-border rounded-lg p-4" data-testid="quote-notes-section">
            <NotesSection entityType="quote" entityId={quoteId} notes={quote.team_notes || []} onChanged={fetchQuote} folder="quote-notes" />
          </div>
        )}

        {/* Save */}
        <div className="flex items-center justify-end gap-3 pt-2">
          <Button variant="outline" onClick={() => navigate("/quotes")}>Cancel</Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-quote-btn">
            {saving ? "Saving..." : isNew ? "Create Quote" : "Save Changes"}
          </Button>
        </div>
      </div>

      {/* Convert Dialog */}
      <Dialog open={showConvert} onOpenChange={setShowConvert}>
        <DialogContent className="max-w-sm" data-testid="convert-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Convert Quote</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground mb-4">Convert this quote to a Job or a Project. Converting to a Project will create a task for each line item.</p>

          {/* Works Order toggle — applies to Job conversion only */}
          <label className={`flex items-start gap-3 border rounded-md p-3 cursor-pointer transition mb-3 ${convertCreateWO ? "border-blue-500 bg-blue-50/50 dark:bg-blue-950/20" : "border-border bg-muted/20"}`} data-testid="quote-convert-create-wo-toggle">
            <input
              type="checkbox"
              checked={convertCreateWO}
              onChange={(e) => setConvertCreateWO(e.target.checked)}
              className="mt-0.5 h-4 w-4"
              data-testid="quote-convert-create-wo-checkbox"
            />
            <div className="flex-1 text-xs">
              <p className="font-medium text-sm">Also create draft Works Order</p>
              <p className="text-muted-foreground mt-0.5">Builds a WO from this quote's <span className="font-medium">{form.line_items?.length || 0} line item{(form.line_items?.length || 0) === 1 ? "" : "s"}</span> (Job conversion only).</p>
            </div>
          </label>

          <div className="space-y-2">
            <Button className="w-full justify-start" variant="outline" onClick={() => handleConvert("job")} disabled={converting} data-testid="convert-to-job-btn">
              <Briefcase size={18} className="mr-2 text-blue-500" /> Convert to Job
            </Button>
            <Button className="w-full justify-start" variant="outline" onClick={() => handleConvert("project")} disabled={converting} data-testid="convert-to-project-btn">
              <Folder size={18} className="mr-2 text-emerald-500" /> Convert to Project
              <span className="text-[10px] text-muted-foreground ml-auto">{form.line_items.length} task{form.line_items.length !== 1 ? "s" : ""}</span>
            </Button>
          </div>
          <DialogFooter>
            <Button variant="ghost" size="sm" onClick={() => setShowConvert(false)}>Cancel</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Client PDF Branding Dialog */}
      <Dialog open={showBrandingDialog} onOpenChange={setShowBrandingDialog}>
        <DialogContent className="max-w-md" data-testid="branding-dialog">
          <DialogHeader>
            <DialogTitle className="font-[Chivo]">Client PDF Branding</DialogTitle>
          </DialogHeader>
          <p className="text-xs text-muted-foreground mb-3">Customise the header for white-label clients. Leave blank to use your default company branding.</p>

          {savedBranding && (savedBranding.company_name || savedBranding.logo_url || savedBranding.footer_text) && (
            <Button variant="outline" size="sm" className="w-full mb-2" onClick={() => setBrandingForm({
              company_name: savedBranding.company_name || "",
              tagline: savedBranding.tagline || "",
              logo_url: savedBranding.logo_url || "",
              footer_text: savedBranding.footer_text || "",
            })} data-testid="load-saved-branding-btn">
              Load Saved Branding{savedBranding.company_name ? ` (${savedBranding.company_name})` : ""}
            </Button>
          )}

          {/* Logo Upload */}
          <div>
            <Label className="text-xs font-semibold">Logo</Label>
            <div
              className="border-2 border-dashed border-border rounded-lg p-3 text-center cursor-pointer hover:border-primary/50 hover:bg-muted/30 transition-colors mt-1"
              onClick={() => brandingFileRef.current?.click()}
              data-testid="branding-logo-dropzone"
            >
              {brandingForm.logo_url ? (
                <div className="flex items-center justify-center gap-3">
                  <img src={brandingForm.logo_url.startsWith("http") ? brandingForm.logo_url : `${process.env.REACT_APP_BACKEND_URL}${brandingForm.logo_url}`} alt="Logo" className="h-10 max-w-[120px] object-contain" />
                  <button onClick={(e) => { e.stopPropagation(); setBrandingForm(f => ({ ...f, logo_url: "" })); }} className="text-xs text-red-500 hover:underline">Remove</button>
                </div>
              ) : (
                <>
                  <p className="text-xs text-muted-foreground">{brandingUploading ? "Uploading..." : "Click to upload logo"}</p>
                  <p className="text-[10px] text-muted-foreground">PNG, JPG (recommended: 300x100px)</p>
                </>
              )}
            </div>
            <input ref={brandingFileRef} type="file" className="hidden" accept=".png,.jpg,.jpeg,.webp,.svg" onChange={e => { if (e.target.files?.[0]) handleBrandingLogoUpload(e.target.files[0]); }} data-testid="branding-logo-input" />
          </div>

          {/* Company Name */}
          <div>
            <Label className="text-xs font-semibold">Company Name</Label>
            <Input value={brandingForm.company_name} onChange={e => setBrandingForm(f => ({ ...f, company_name: e.target.value }))} placeholder="e.g. Wilson Property Management Ltd" className="mt-1" data-testid="branding-company-name" />
          </div>

          {/* Footer Text */}
          <div>
            <Label className="text-xs font-semibold">Footer Text</Label>
            <Textarea value={brandingForm.footer_text} onChange={e => setBrandingForm(f => ({ ...f, footer_text: e.target.value }))} placeholder="e.g. Quote prepared by Pete, Wilson Property Management" rows={2} className="mt-1 text-xs" data-testid="branding-footer-text" />
          </div>

          <p className="text-[10px] text-muted-foreground">
            {form.client_id ? "Branding will be saved for this client and auto-filled next time." : "Select a client on the quote to save branding for reuse."}
          </p>

          <DialogFooter>
            <Button variant="outline" size="sm" onClick={() => { handleDownloadPdf("client"); setShowBrandingDialog(false); }} data-testid="branding-default-btn">
              Use Default
            </Button>
            <Button size="sm" onClick={handleBrandedPdfDownload} data-testid="branding-download-btn">
              <FilePdf size={14} className="mr-1" /> Download Client PDF
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Email Quote Dialog (opt-in white-label feature) */}
      <QuoteEmailDialog quoteId={quoteId} open={showEmailDialog} onOpenChange={setShowEmailDialog} onSent={fetchQuote} />
      <AiScopeDialog key={aiKind || "closed"} open={!!aiKind} onOpenChange={(v) => { if (!v) setAiKind(""); }} kind={aiKind || "scope"} quoteId={isNew ? "" : quoteId}
        existingScope={form.scope_of_works} existingCaveats={form.caveats}
        quote={{ title: form.title, client_name: form.client_name, client_company: form.client_company, property_name: form.property_name, street_address: form.street_address, town_city: form.town_city, postcode: form.postcode, line_items: form.line_items }}
        onApply={({ kind, text, mode, caveats }) => setForm(f => {
          const merge = (cur, add) => (mode === "append" && cur.trim() ? `${cur.trimEnd()}\n${add}` : add);
          if (kind === "caveats") return { ...f, caveats: merge(f.caveats, text) };
          return { ...f, scope_of_works: merge(f.scope_of_works, text), caveats: caveats ? (f.caveats.trim() ? `${f.caveats.trimEnd()}\n${caveats}` : caveats) : f.caveats };
        })} />

      {/* Reverse pricing calculator */}
      <ReverseCalcDialog
        open={reverseCalcOpen}
        onOpenChange={setReverseCalcOpen}
        initialMarkupA={form.markup_a_pct || 0}
        initialMarkupB={form.markup_b_pct || 0}
        initialMarkupALabel={form.markup_a_label || "Markup A"}
        initialMarkupBLabel={form.markup_b_label || "Markup B"}
        initialVatEnabled={!!form.vat_enabled}
        initialVatRate={form.vat_rate || 20}
        initialTotal={Number(grandTotal) > 0 ? Number(grandTotal) : 1000}
        contextLabel={form.reference ? `${form.reference} · ${form.markup_a_pct || 0}% + ${form.markup_b_pct || 0}%` : ""}
      />
    </div>
  );
}
